// data/strategic-playbook/collections.js
// ════════════════════════════════════════════════════════════════════
// Curated reading paths through the corpus. Each collection is a
// hand-picked ordered sequence of entries (findings, tectonic shifts,
// dossiers) that together tell a thematic story. Designed to give a
// new reader an "I want to understand X" entry point without forcing
// them to assemble it themselves.
//
// Schema:
//   {
//     id, slug, name, eyebrow, description,
//     intro,                           // 1-2 paragraph editorial frame
//     entries: [{ kind, ref, note? }], // ordered reading list
//   }
//
// kind values:
//   'finding'  → ref = citation handle like 'lab:finding/popdec/2026/.../v1'
//   'shift'    → ref = shift slug (e.g., 'demographics')
//   'dossier'  → ref = dossier id from data/strategic-playbook/dossiers
//   'brief'    → ref = brief slug
//
// Each entry's `note` (optional) is a one-liner explaining why it sits
// where it does in the path — the connective tissue between entries.
// ════════════════════════════════════════════════════════════════════

export const COLLECTIONS = [
  {
    id: 'fertility-deep-dive',
    slug: 'fertility-deep-dive',
    name: 'Fertility & Population Decline — the deep dive',
    eyebrow: 'Curated path · 7 entries',
    description: 'The full popdec corpus, sequenced. Macro frame → static thesis → dynamic mechanism → public falsifier → positive case → generalization.',
    intro: 'The thesis being tested across this path: pronatalist policy compensates at margins, but cannot create the cultural-structural conditions that lift fertility. Necessary-but-insufficient. Read in order — each finding compounds on the prior.',
    entries: [
      {
        kind: 'shift',
        ref: 'demographics',
        note: 'Start here. The macro frame — what\'s shifting, who benefits, what to watch.',
      },
      {
        kind: 'finding',
        ref: 'lab:finding/popdec/2026/korea-hungary-divergence/v1',
        note: 'The static thesis: Hungary spent 6.2% GDP and lifted TFR temporarily; Korea didn\'t. The wedge isn\'t spending — it\'s structural-cultural.',
      },
      {
        kind: 'finding',
        ref: 'lab:finding/popdec/2026/korea-lockin/v1',
        note: 'The dynamic version. Six branch points in Korea\'s decline; three were levers, three were lockings.',
      },
      {
        kind: 'finding',
        ref: 'lab:finding/popdec/2026/positive-case-search/v1',
        note: 'The falsification test. Eight OECD candidates on a public clock; the load-bearing thesis under public falsification through 2035.',
      },
      {
        kind: 'finding',
        ref: 'lab:finding/popdec/2026/israel-haredi-floor/v1',
        note: 'The "exception" that documents the rule. Israel\'s sustained 2.85 TFR runs on religious-community substrate, not policy.',
      },
      {
        kind: 'finding',
        ref: 'lab:finding/popdec/2026/refugia-template/v1',
        note: 'The abstraction. Where dense substrate exists, demographic resilience clusters — independent of ideology or policy.',
      },
      {
        kind: 'finding',
        ref: 'lab:finding/popdec/2026/substrate-symmetric-refugia/v1',
        note: 'The generalization. Structural-cultural lens extended beyond fertility to the broader resilience question.',
      },
    ],
  },

  {
    id: 'historical-analogs',
    slug: 'historical-analogs',
    name: 'The historical analog library',
    eyebrow: 'Curated path · 6 entries',
    description: 'Six resolved tectonic shifts whose outcomes inform how we read active ones. Read this when you want to pattern-match a current shift to a past one.',
    intro: 'Each historical shift carries authored content with full data: era charts, beneficiaries-then-known, lessons extracted. The lessons are the analog payoff — they generalize to active shifts that resemble the historical shape.',
    entries: [
      {
        kind: 'shift',
        ref: 'fall-of-rome-west',
        note: '180-476 AD. Slow structural decay over centuries. Currency debasement (denarius silver 95% → 2%) was the canary.',
      },
      {
        kind: 'shift',
        ref: 'black-death',
        note: '1347-1351. Sudden mortality shock. Real wages doubled in 50 years; legislation against the labor scarcity premium failed.',
      },
      {
        kind: 'shift',
        ref: 'industrial-revolution',
        note: '1760-1840. General-purpose technology shifts initially transfer wealth from labor to capital; worker benefits arrive ~50 years later via political reform.',
      },
      {
        kind: 'shift',
        ref: 'ww2-baby-boomers',
        note: '1939-1970. War-time industrial capacity, post-war redirected. Demographic wave (4.3M peak births in 1957) drove 60+ years of compounding economic structure.',
      },
      {
        kind: 'shift',
        ref: 'cold-war-end',
        note: '1989-2001. Hegemonic moment is transitional; the structural beneficiaries generate the reaction that ends the window.',
      },
      {
        kind: 'shift',
        ref: 'internet-boom',
        note: '1995-2010. Cost-curve break → boom → 78% drawdown → recovery. Surviving 70-90% drawdowns is a feature of the cycle, not a defect.',
      },
    ],
  },

  {
    id: 'asymmetric-positioning',
    slug: 'asymmetric-positioning',
    name: 'Asymmetric positioning — finding the bet',
    eyebrow: 'Curated path · 4 entries',
    description: 'How to find positions where the upside is asymmetric and the downside is bounded. Strategic Playbook entries on bet-sizing logic.',
    intro: 'The Asymmetric Underdog finding is the load-bearing claim; the related shifts give the macro context where the pattern shows up most reliably. Read them as a unit.',
    entries: [
      {
        kind: 'finding',
        ref: 'lab:finding/strategic/2026/asymmetric-underdog-inversion/v1',
        note: 'The pattern across cases (Form 4). Three signals separate real bets from cosplay; one failure case (Texas Tech 2018) confirms the mechanism.',
      },
      {
        kind: 'shift',
        ref: 'us-china-decoupling',
        note: 'Structural decoupling generates asymmetric positions in the reshoring corridor (Mexico, Vietnam, India) that are still partially priced.',
      },
      {
        kind: 'shift',
        ref: 'defense-industrial-base',
        note: '8-15 year capacity rebuild. Multi-year contract authority + new-entrant procurement reform creates the asymmetry.',
      },
      {
        kind: 'shift',
        ref: 'humanoid-robotics',
        note: 'Year 1-2. Cost curve hasn\'t broken yet — the asymmetric window is positioning before it does.',
      },
    ],
  },
];

export function listCollections() {
  return COLLECTIONS;
}

export function getCollection(slug) {
  return COLLECTIONS.find((c) => c.slug === slug) || null;
}
